import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import { HR } from 'foundations-components/transitional/components/HR';
import { useSubtotals } from '../../context/SubtotalsProvider';
import TaxesAndFees from './TaxesAndFees';
import { OrderLevelDiscount } from './OrderLevelDiscount';
import LineItemTaxesTotal from './LineItemTaxesTotal';
import { isLineItemWithTax } from '../../types/utils/typeguards';
import CalculateAtCheckoutTaxes from './CalculateAtCheckoutTaxes';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Text } from 'foundations-components/components/Text';
import { LEFT_GRID_SIZE, RIGHT_GRID_SIZE } from '../../constants/Sizes';
import partition from 'hs-lodash/partition';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// used for one-time, order-level discounts (not discount codes)
export function SubtotalAdjustments({
  session
}) {
  var _session$lineItems;
  // these values are dynamic because open-amount line items (price is editable by the buyer)
  // and percentage based discounts mean that the discount value in dollars can change during checkout
  const {
    loading,
    firstPaymentSubtotal,
    firstPaymentSubtotalBeforeTax,
    adjustmentAmounts
  } = useSubtotals();
  const {
    currencyCode,
    orderDiscount,
    isDiscountCodeEnabled
  } = session;
  const hasLineItemTaxes = (_session$lineItems = session.lineItems) === null || _session$lineItems === void 0 ? void 0 : _session$lineItems.some(isLineItemWithTax);
  const hasCalculateAtCheckoutTaxes = session.isAutomatedSalesTaxEnabled;
  const shouldShowSubtotals = !isDiscountCodeEnabled || hasLineItemTaxes;
  const [checkoutFees, otherAdjustments] = useMemo(() => partition(adjustmentAmounts, adjustment => adjustment.adjustmentType === 'CHECKOUT_FEE'), [adjustmentAmounts]);
  const subtotalValue = useMemo(() => {
    if (hasLineItemTaxes || hasCalculateAtCheckoutTaxes) {
      return firstPaymentSubtotalBeforeTax || 0;
    }
    return firstPaymentSubtotal || 0;
  }, [hasLineItemTaxes, hasCalculateAtCheckoutTaxes, firstPaymentSubtotal, firstPaymentSubtotalBeforeTax]);
  if (!orderDiscount && !adjustmentAmounts.length && !hasLineItemTaxes && !hasCalculateAtCheckoutTaxes) {
    return null;
  }
  if (loading) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "checkout-subtotal-adjustments--loading",
      children: /*#__PURE__*/_jsx(LoadingDots, {
        role: "progressbar",
        minHeight: 100
      })
    });
  }
  const maybeOrderDiscount = orderDiscount && /*#__PURE__*/_jsx(OrderLevelDiscount, {
    currencyCode: currencyCode,
    orderDiscount: orderDiscount
  });
  const maybeCalculateAtCheckoutTaxes = hasCalculateAtCheckoutTaxes && /*#__PURE__*/_jsx(CalculateAtCheckoutTaxes, {
    session: session,
    currencyCode: currencyCode
  });
  const maybeLineItemTaxes = hasLineItemTaxes && !hasCalculateAtCheckoutTaxes && /*#__PURE__*/_jsx(LineItemTaxesTotal, {
    subtotalsPublicResponse: session.subtotalsPublicResponse,
    currencyCode: currencyCode
  });
  const maybeTaxesAndFees = otherAdjustments.length > 0 && /*#__PURE__*/_jsx(TaxesAndFees, {
    adjustments: otherAdjustments,
    currencyCode: currencyCode
  });
  const maybeCheckoutFees = checkoutFees.length > 0 && /*#__PURE__*/_jsx(TaxesAndFees, {
    adjustments: checkoutFees,
    currencyCode: currencyCode
  });
  const maybeSubtotal = shouldShowSubtotals && /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(GridItem, {
      size: LEFT_GRID_SIZE,
      marginBlockEnd: "500",
      "data-test-id": "checkout-subtotal",
      children: /*#__PURE__*/_jsx(Text, {
        as: "h6",
        textAlign: "start",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.subtotal"
        })
      })
    }), /*#__PURE__*/_jsx(GridItem, {
      size: RIGHT_GRID_SIZE,
      marginBlockEnd: "500",
      "data-test-id": "checkout-order-level-discount--subtotal",
      children: /*#__PURE__*/_jsx(Text, {
        as: "h6",
        textAlign: "end",
        children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
          value: subtotalValue,
          currencyCode: currencyCode
        })
      })
    })]
  });
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "checkout-subtotal-adjustments",
    children: [/*#__PURE__*/_jsxs(Grid, {
      paddingInline: "600",
      children: [maybeSubtotal, maybeOrderDiscount, maybeTaxesAndFees, maybeLineItemTaxes, maybeCalculateAtCheckoutTaxes, maybeCheckoutFees]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "medium"
    })]
  });
}